// Carol Care brand mark + wordmark.
//
// The Mark is a small olive sprig on a warm cream rounded square — matches
// the browser favicon and the home-screen app icon so the in-app header
// visually anchors to the same identity users see elsewhere.

import { cn } from "../lib/cn";

interface MarkProps {
  size?: number;
  className?: string;
}

export function Mark({ size = 28, className }: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      role="img"
      aria-label="Carol Care logo"
      className={cn("inline-block shrink-0", className)}
    >
      <rect width="40" height="40" rx="9" fill="#F2EFE8" />
      <g fill="#0B1A33">
        <path
          d="M20 8 C20 14, 19 24, 17 32"
          stroke="#0B1A33"
          strokeWidth="1"
          strokeLinecap="round"
          fill="none"
        />
        <ellipse cx="16" cy="13" rx="3" ry="1.2" transform="rotate(-35 16 13)" />
        <ellipse cx="25" cy="15" rx="3" ry="1.2" transform="rotate(35 25 15)" />
        <ellipse cx="14" cy="19" rx="3.6" ry="1.3" transform="rotate(-22 14 19)" />
        <ellipse cx="26" cy="21" rx="3.6" ry="1.3" transform="rotate(22 26 21)" />
        <ellipse cx="13" cy="26" rx="4" ry="1.4" transform="rotate(-12 13 26)" />
        <circle cx="23" cy="17" r="1.6" />
      </g>
    </svg>
  );
}

interface WordmarkProps {
  size?: "sm" | "md" | "lg";
  /** Set to false to render the wordmark without the icon. */
  withMark?: boolean;
  className?: string;
}

const sizes = {
  sm: { mark: 22, text: "text-base" },
  md: { mark: 26, text: "text-lg" },
  lg: { mark: 34, text: "text-2xl" },
} as const;

export function Wordmark({ size = "md", withMark = true, className }: WordmarkProps) {
  const s = sizes[size];
  return (
    <span className={cn("inline-flex items-center gap-2 select-none", className)}>
      {withMark && <Mark size={s.mark} />}
      <span className={cn("font-semibold tracking-tight text-text", s.text)}>
        Carol<span className="text-text-muted font-normal"> Care</span>
      </span>
    </span>
  );
}
